import React, { useState, useEffect, useRef, useMemo } from "react";
import styles from "./LoanAppModal.module.css";
import api, { withCompanyId } from "../../Components/API/Api";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import crop from "../../Assets/repoort.png";
import verified from "../../Assets/verified.svg";
import CurrencyInput from 'react-currency-input-field';

function LoanAppModal({  isOpen,
    onClose,
    onSuccess,
    mode = "new",
    requestData = null }) {
  const navigate = useNavigate();

  // Refs for click-outside detection
  const memberRef = useRef(null);
  const bankRef = useRef(null);

  // ─── State ───────────────────────────────────────────────
  const [selectedMember, setSelectedMember] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [memberOptions, setMemberOptions] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);

  const [selectedLoanProducts, setSelectedLoanProducts] = useState("");
  const [loanProducts, setLoanProducts] = useState([]);
  const [loanProductsLoading, setLoanProductsLoading] = useState(false);

  const [transactionDate, setTransactionDate] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [currencyOptions, setCurrencyOptions] = useState([]);

  const [duration, setDuration] = useState("");
  const [loanInterest, setLoanInterest] = useState("");

  const [paymentModes, setPaymentModes] = useState([]);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState("");

  // Bank account (searchable)
  const [debitAccount, setDebitAccount] = useState(""); // stores the ID
  const [debitSearch, setDebitSearch] = useState("");
  const [showDebitDropdown, setShowDebitDropdown] = useState(false);
  const [creditAccounts, setCreditAccounts] = useState([]);
  const [debitAccountsLoading, setDebitAccountsLoading] = useState(false);

  const [totalAmount, setTotalAmount] = useState("");
  const [description, setDescription] = useState(""); // reference number

  const [supportingDocument, setSupportingDocument] = useState(null);
  const [supportingError, setSupportingError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // ─── Click-outside handler for dropdowns ────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close member dropdown if clicking outside
      if (memberRef.current && !memberRef.current.contains(event.target)) {
        setShowMemberDropdown(false);
      }
      // Close bank dropdown if clicking outside
      if (bankRef.current && !bankRef.current.contains(event.target)) {
        setShowDebitDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ─── Computed summary via useMemo ──────────────────────
  const summary = useMemo(() => {
    const principal = Number(String(totalAmount || "0").replace(/,/g, ""));
    const rate = Number(loanInterest || 0);
    const interest = (rate / 100) * principal;
    const totalRepayment = principal + interest;
    const months = Number(duration || 0);
    const monthly = months ? totalRepayment / months : 0;

    return {
      interest: isNaN(interest) ? 0 : interest,
      monthlyDeduction: isNaN(monthly) ? 0 : monthly,
      totalRepayment: isNaN(totalRepayment) ? 0 : totalRepayment,
    };
  }, [totalAmount, loanInterest, duration]);

  // ─── Fetch: Members ────────────────────────────────────
  useEffect(() => {
    const fetchMembers = async () => {
      setMemberLoading(true);
      try {
        const res = await api.get('/v1/member', { params: { name: 'payment' } });
        if (res.data && Array.isArray(res.data.data)) {
          setMemberOptions(res.data.data);
        } else {
          setMemberOptions([]);
        }
      } catch (err) {
        setMemberOptions([]);
      } finally {
        setMemberLoading(false);
      }
    };
    if (isOpen) fetchMembers();
  }, [isOpen]);

  // ─── Fetch: Loan Products ──────────────────────────────
  useEffect(() => {
    const fetchLoanProducts = async () => {
      setLoanProductsLoading(true);
      try {
        const res = await api.get('/v1/loan_account');
        if (res.data && Array.isArray(res.data.data)) {
          setLoanProducts(res.data.data);
        } else {
          setLoanProducts([]);
        }
      } catch (err) {
        setLoanProducts([]);
      } finally {
        setLoanProductsLoading(false);
      }
    };
    if (isOpen) fetchLoanProducts();
  }, [isOpen]);

  // ─── Fetch: Currencies ──────────────────────────────────
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const res = await api.get('/v1/fetch-all-currencies');
        if (res.data && Array.isArray(res.data.data)) {
          setCurrencyOptions(res.data.data);
          const ngn = res.data.data.find(c => c.code === "NGN");
          if (ngn) setSelectedCurrency(ngn.id);
        }
      } catch (err) {
        console.error("Error fetching currencies:", err);
      }
    };
    if (isOpen) fetchCurrencies();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (mode === "additional" && requestData) {

        setSelectedMember(requestData.member.id);
        setMemberSearch(requestData.member.name);

        setSelectedLoanProducts(requestData.loan.id);

        setLoanInterest(requestData.loan.interest_rate);

    } else {

        resetFormFields();

    }

}, [isOpen, mode, requestData]);

  // ─── Fetch: Payment Modes ──────────────────────────────
  useEffect(() => {
    const fetchPaymentModes = async () => {
      try {
        const res = await api.get('/v1/income/get-payment-method');
        if (res.data && Array.isArray(res.data.data)) {
          setPaymentModes(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching payment modes:", err);
      }
    };
    if (isOpen) fetchPaymentModes();
  }, [isOpen]);

  // ─── Fetch: Bank Accounts ──────────────────────────────
  useEffect(() => {
    const fetchBankAccounts = async () => {
      setDebitAccountsLoading(true);
      try {
        const res = await api.get('/v1/bank-accounts');
        if (res.data && Array.isArray(res.data.data)) {
          const accounts = res.data.data.map((acc) => ({
            id: acc.id,
            label: acc.gl_name || acc.name || acc.account_name,
            gl_code: acc.gl_code || acc.code,
          }));
          setCreditAccounts(accounts);
        }
      } catch (err) {
        console.error("Error fetching bank accounts:", err);
      } finally {
        setDebitAccountsLoading(false);
      }
    };
    if (isOpen) fetchBankAccounts();
  }, [isOpen]);

  // ─── Helper: reset ──────────────────────────────────────
 const resetFormFields = () => {

    setSelectedMember("");
    setMemberSearch("");

    setSelectedLoanProducts("");

    setTransactionDate("");

    setDuration("");

    setLoanInterest("");

    setTotalAmount("");

    setDescription("");

    setDebitAccount("");

    setDebitSearch("");

    setSupportingDocument(null);

    setSupportingError("");

    setShowMemberDropdown(false);

    setShowDebitDropdown(false);
//     setLoanMode("new");
// setRequestLoanData(null);

};

  // ─── File handler ───────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) { setSupportingDocument(null); setSupportingError(""); return; }
    if (file.type !== 'application/pdf') {
      setSupportingError('Only PDF files are allowed.');
      setSupportingDocument(null);
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setSupportingError('File must be 2MB or smaller.');
      setSupportingDocument(null);
      return;
    }
    setSupportingError("");
    setSupportingDocument(file);
  };

  

  // ─── Submit ─────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedMember || !selectedLoanProducts || !transactionDate || !duration || !totalAmount) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete form",
        text: "Please fill in all required fields.",
      });
      return;
    }

    setIsSaving(true);
    try {
      const companyId = localStorage.getItem("company_id") || localStorage.getItem("companyId");
      const formData = new FormData();
      const endpoint =
    mode === "additional"
        ? "/v1/member/additional_loan_request"
        : "/v1/member_loan_application";

      formData.append("company_id", companyId);
      formData.append("member_name", selectedMember);
      formData.append("loan_name", selectedLoanProducts);
      formData.append("transaction_date", transactionDate);
      formData.append("currency", selectedCurrency);
      formData.append("loan_interest", loanInterest);
      formData.append("principal_amount", Number(String(totalAmount || "0").replace(/,/g, "")));
      formData.append("interest_amount", summary.interest);
      formData.append("total_repayment", summary.totalRepayment);
      formData.append("duration", duration);
      formData.append("monthly_deduction", summary.monthlyDeduction);
      formData.append("bank", debitAccount);
      formData.append("reference_number", description);
      if (mode === "additional") {
    formData.append("original_id", requestData.id);
}
      if (supportingDocument) formData.append("file", supportingDocument);

      const response = await api.post(
    endpoint,
    withCompanyId(formData),
    {
        headers:{
            "Content-Type":"multipart/form-data"
        }
    }
);

      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          imageUrl: verified,
          title: "Loan Application Submitted",
          text: response.data?.message || "Loan application submitted successfully.",
          confirmButtonColor: "#16A34A",
        }).then(() => {
          resetFormFields();
          if (onSuccess) onSuccess();
          onClose();
        });
      }
    } catch (error) {
      console.error(error);
      const msg = typeof error.response?.data?.message === "object"
        ? Object.values(error.response.data.message).flat().join(", ")
        : error.response?.data?.message || "An error occurred.";
      Swal.fire({
        imageUrl: crop,
        title: "Submission Failed",
        text: msg,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  // ─── Filter helpers ─────────────────────────────────────
  const filteredMembers = memberOptions.filter((m) =>
    (m.name || "").toLowerCase().includes(memberSearch.toLowerCase())
  );
  const filteredAccounts = creditAccounts.filter((acc) =>
    (acc.label || "").toLowerCase().includes(debitSearch.toLowerCase())
  );

  

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>

        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div>
              <h2 className={styles.title}>
    {mode === "additional"
        ? "Request Additional Loan"
        : "Loan Application"}
</h2>

<p className={styles.subtitle}>
    {mode === "additional"
        ? "Complete the form to request an additional loan."
        : "Complete all fields to process this loan"}
</p>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={styles.body}>

          {/* ── 3‑Column Grid ──────────────────────────── */}
          {/* <div className={styles.grid3}> */}

            {/* 1. Member (Searchable) */}
            {/* <div className={styles.firstRow} */}
            <div className={styles.field} ref={memberRef}>
              <label className={styles.label}>Member Name</label>
              <div className={styles.searchDropdownWrap}>
                <div className={styles.inputWithIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="Search or select member…"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    onFocus={() => {
    if (mode !== "additional")
        setShowMemberDropdown(true);
}}
                    onClick={() => {
    if (mode !== "additional")
        setShowMemberDropdown(true);
}}
                    disabled={
        memberLoading ||
        mode === "additional"
    }
                  />
                </div>
                {showMemberDropdown && (
                  <div className={styles.dropdownList}>
                    {memberLoading ? (
                      <div className={styles.dropdownLoading}><Spinner size="sm" animation="border" /></div>
                    ) : filteredMembers.length === 0 ? (
                      <div className={styles.dropdownEmpty}>No members found</div>
                    ) : (
                      filteredMembers.map((m) => (
                        <div
                          key={m.id}
                          className={styles.dropdownItem}
                          onMouseDown={() => {
                            setSelectedMember(m.id);
                            setMemberSearch(m.name || "");
                            setShowMemberDropdown(false);
                          }}
                        >
                          {m.name}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

              <div className={styles.field} style={{marginTop: '1rem'}}>
              <label className={styles.label}>Loan Product</label>
              <div className={styles.selectWrap}>
                <select
                  className={styles.select}
                  value={selectedLoanProducts}
                  disabled={mode === "additional"}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedLoanProducts(id);
                    const loan = loanProducts.find((l) => l.id.toString() === id);
                    if (loan) setLoanInterest(loan.interest_rate);
                  }}
                >
                  <option value="">Select product</option>
                  {loanProductsLoading ? (
                    <option>Loading…</option>
                  ) : (
                    loanProducts.map((t) => (
                      <option key={t.id} value={t.id}>{t.loan_name || t.type_name}</option>
                    ))
                  )}
                </select>
                {/* <span className={styles.selectArrow}>▾</span> */}
              </div>
            </div>

               <div className={styles.field} style={{marginTop: '1rem'}}>
              <label className={styles.label}>Principal Amount (₦)</label>
              <CurrencyInput
                className={styles.input}
                style={{width: 480}}
                decimalsLimit={2}
                placeholder="0.00"
                value={totalAmount}
                onValueChange={(val) => setTotalAmount(val || "")}
              />
            </div>

            {/* 2. Loan Product */}
            <div className={styles.fstGrp}>

            
          

                 {/* 3. Date */}
            <div className={styles.field}>
              <label className={styles.label}>Date</label>
              <input
                type="date"
                className={styles.input}
                value={transactionDate}
                onChange={(e) => setTransactionDate(e.target.value)}
              />
            </div>

             <div className={styles.field}>
              <label className={styles.label}>Duration (months)</label>
              <input
                type="number"
                className={styles.input}
                placeholder="e.g. 12"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Interest rate (%)</label>
              <input
                type="number"
                className={styles.input}
                value={loanInterest}
                disabled
                placeholder="%"
              />
            </div>


            </div>

       

            {/* 4. Currency */}
            {/* <div className={styles.field}>
              <label className={styles.label}>Currency</label>
              <div className={styles.selectWrap}>
                <select
                  className={styles.select}
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                >
                  <option value="">Select currency</option>
                  {currencyOptions.map((c) => (
                    <option key={c.id} value={c.id}>{c.code || c.name}</option>
                  ))}
                </select>
                <span className={styles.selectArrow}>▾</span>
              </div>
            </div> */}

            {/* 5. Duration */}

           

            {/* 7. Bank Account (Searchable) */}
            {/* <div className={styles.field} ref={bankRef}>
              <label className={styles.label}>Bank Account</label>
              <div className={styles.searchDropdownWrap}>
                <div className={styles.inputWithIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="Search or select bank account…"
                    value={debitSearch}
                    onChange={(e) => setDebitSearch(e.target.value)}
                    onFocus={() => setShowDebitDropdown(true)}
                    onClick={() => setShowDebitDropdown(true)}
                    disabled={debitAccountsLoading}
                  />
                </div>
                {showDebitDropdown && (
                  <div className={styles.dropdownList}>
                    {debitAccountsLoading ? (
                      <div className={styles.dropdownLoading}><Spinner size="sm" animation="border" /></div>
                    ) : filteredAccounts.length === 0 ? (
                      <div className={styles.dropdownEmpty}>No accounts found</div>
                    ) : (
                      filteredAccounts.map((acc) => (
                        <div
                          key={acc.id}
                          className={styles.dropdownItem}
                          onMouseDown={() => {
                            setDebitAccount(acc.id);
                            setDebitSearch(acc.label || "");
                            setShowDebitDropdown(false);
                          }}
                        >
                          {acc.label}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div> */}

            {/* 8. Principal Amount */}
         

      

            {/* 9. Reference No. */}
           
                   <div className={styles.fstGrp}>

             <div className={styles.field}>
              <label className={styles.label}>Interest Amount (₦)</label>
              <input
                type="text"
                className={`${styles.input} ${styles.readOnlyInput}`}
                value={summary.interest.toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                readOnly
                disabled
              />
            </div>

           
 <div className={styles.field}>
              <label className={styles.label}>Monthly Deduction (₦)</label>
              <input
                type="text"
                className={`${styles.input} ${styles.readOnlyInput}`}
                value={summary.monthlyDeduction.toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                readOnly
                disabled
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Total Loan (₦)</label>
              <input
                type="text"
                className={`${styles.input} ${styles.readOnlyInput}`}
                value={summary.totalRepayment.toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                readOnly
                disabled
              />
            </div>
           
            
            </div>

            <div className={styles.scndGrp}>
               <div className={styles.field}>
              <label className={styles.label}>Reference / Loan No.</label>
              <input
                type="text"
                className={styles.input}
                placeholder="e.g. LN-2026-0042"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className={styles.field} >
            <label className={styles.label}>Supporting document (PDF)</label>
            <label className={styles.fileZone}>
              <input
                type="file"
                accept="application/pdf"
                style={{ display: "none", height: 35 }}
                onChange={handleFileChange}
                
              />
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span>
                {supportingDocument ? supportingDocument.name : "Click to upload PDF"}
              </span>
              {supportingDocument && (
                <button
                  type="button"
                  className={styles.removeFileBtn}
                  onClick={(e) => {
                    e.preventDefault();
                    setSupportingDocument(null);
                    setSupportingError("");
                  }}
                >
                  Remove
                </button>
              )}
            </label>
            {supportingError && <p className={styles.errorMsg}>{supportingError}</p>}
          </div>

            </div>

            

            {/* ─── NEW AUTO‑CALCULATED FIELDS ────────────── */}
           

           

            
          {/* </div> */}

          {/* ── File Upload ────────────────────────────── */}
          
        </div>

        {/* ── Footer ────────────────────────────────────── */}
        <div className={styles.footer}>
          <div className={styles.footerNote}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Data is encrypted and stored securely
          </div>
          <div className={styles.footerActions}>
            <button type="button" className={styles.resetBtn} onClick={resetFormFields}>
              Reset
            </button>
            <button
              type="button"
              className={styles.submitBtn}
              disabled={isSaving}
              onClick={handleSubmit}
            >
              {isSaving ? (
                <span className={styles.savingRow}>
                  <Spinner animation="border" size="sm" />
                  Saving…
                </span>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                    <polyline points="17 21 17 13 7 13 7 21" />
                    <polyline points="7 3 7 8 15 8" />
                  </svg>
                  Submit Loan
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoanAppModal;